/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { SaveItemMemo } from 'src/app/store/memo/memo.actions';
import { ComponentData } from '../../../model/memo.model';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { EditorService } from '../../../service/editor.service';
import { ceil } from 'lodash';
import { EditorModule } from '@tinymce/tinymce-angular';

const DEFAULT_REMAIN = 1000;

export type MemoEditorFormMode = 'memo' | 'normal' | 'contract_tc';

@Component({
  selector: 'app-memo-editor-detail',
  templateUrl: './memo-editor-detail.component.html',
  styleUrls: ['./memo-editor-detail.component.scss'],
  standalone: true,
  imports: [
    EditorModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class MemoEditorDetailComponent {
  memoData: any;
  config: any;
  configEditorMail: any;
  configContractTC: any;
  isRefreshing = false;
  @Input() inputComponent: ComponentData;
  @Input() control: UntypedFormControl;
  @Input() formMode: MemoEditorFormMode = 'memo';
  @Output() charRemainChange = new EventEmitter<number>();
  remain = DEFAULT_REMAIN;

  constructor(
    private store: Store,
    private translate: TranslateService,
    private editorService: EditorService,
  ) {
    this.store.subscribe((state) => {
      this.memoData = state.memoCreationData;
    });
    this.config = {
      height: 500,
      base_url: '/tinymce',
      suffix: '.min',
      skin_url: '/tinymce/skins/lightgray',
      selector: 'textarea',
      branding: false,
      menu: {
        edit: {
          title: 'Edit',
          items:
            'undo redo | cut copy paste | selectall | searchreplace',
        },
        insert: {
          title: 'Insert',
          items:
            'image link template codesample inserttable | charmap emoticons hr | ' +
            '| insertdatetime',
        },
        format: {
          title: 'Format',
          items:
            'bold italic underline strikethrough superscript subscript codeformat | ' +
            'formats blockformats fontformats fontsizes align lineheight | forecolor backcolor | ' +
            'removeformat',
        },
        tools: {
          title: 'Tools',
          items: 'spellchecker spellcheckerlanguage | code wordcount',
        },
        table: {
          title: 'Table',
          items:
            'inserttable | cell row column | tableprops deletetable',
        },
      },
      statusbar: false,
      // using ../ sometimes it goes to wrong path.
      content_css: '/assets/scss/content-editor.css',
      width: '95%',
      language:
        this.translate.getCurrentLang() === 'en' ? 'en' : 'th_TH',
      language_url:
        this.translate.getCurrentLang() === 'en'
          ? null
          : 'assets/langs/th_TH.js',
      plugins: [
        'advlist autolink lists link image charmap print preview anchor',
        'searchreplace visualblocks code fullscreen',
        'insertdatetime media table code help wordcount imagetools',
        'textcolor paste',
      ],
      paste_data_images: true,
      paste_remove_styles_if_webkit: false,
      paste_preprocess: (editor: any, args: any) => {
        args.content = this.adjustTableWidthFromPaste(args.content);
      },
      toolbar:
        'styleselect fontsizeselect | bold italic underline | \
         alignleft aligncenter alignright alignjustify | \
         bullist numlist | removeformat',
      file_picker_types: 'image',
      file_picker_callback: (cb: any) => {
        const input: any = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = (e?: HTMLInputEvent | any) => {
          const file = e.target.files[0];

          const reader = new FileReader();
          reader.onload = (event: any) => {
            const blobInfo = event.target.result;
            cb(blobInfo, { title: file.name });
          };
          reader.readAsDataURL(file);
        };

        input.click();

        interface HTMLInputEvent extends Event {
          target: HTMLInputElement & EventTarget;
        }
      },
      setup: (editor: any) => {
        editor.on('ObjectResized', (e: any) => {
          if (e.target.nodeName === 'IMG') {
            const selectedImage = editor.selection.getNode();
            editor.dom.setStyle(
              selectedImage,
              'width',
              e.width + 'px',
            );
            editor.dom.setStyle(
              selectedImage,
              'height',
              e.height + 'px',
            );

            selectedImage.removeAttribute('width');
            selectedImage.removeAttribute('height');
          }
        });
      },
      init_instance_callback: (editor: any) => {
        editor.on('ObjectSelected', (e: any) => {
          if (e.target.nodeName === 'IMG') {
            const selectedImage = editor.selection.getNode();
            editor.dom.setStyle(
              selectedImage,
              'width',
              e.target.width + 'px',
            );
            editor.dom.setStyle(
              selectedImage,
              'height',
              e.target.height + 'px',
            );

            selectedImage.removeAttribute('width');
            selectedImage.removeAttribute('height');
          }
        });
        editor.on('keydown', (e: any) => {
          if (e.keyCode === 9) {
            e.preventDefault();
            // @ts-ignore
            tinymce.activeEditor.execCommand(
              'mceInsertContent',
              false,
              '&nbsp;&nbsp;&nbsp;&nbsp;',
            );
          }
        });
      },
      automatic_uploads: true,
      fontsize_formats:
        '8pt 10pt 12pt 14pt 16pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt',
      content_style: 'body {font-size: 14pt;}',
      table_toolbar:
        'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow |' +
        ' tableinsertcolbefore tableinsertcolafter tabledeletecol',
      skin: 'lightgray',
      relative_urls: false,
      remove_script_host: false,
      images_upload_handler: (
        blobInfo: any,
        success: any,
        failure: any,
      ) => {
        this.uploadEditorImage(blobInfo, success, failure);
      },
    };
    this.configEditorMail = {
      height: 300,
      base_url: '/tinymce',
      suffix: '.min',
      icons: 'material', // use icon pack
      skin_url: '/tinymce/skins/lightgray',
      selector: '.editor>textarea',
      branding: false,
      menu: {
        edit: {
          title: 'Edit',
          items:
            'undo redo | cut copy paste | selectall | searchreplace',
        },
        tools: {
          title: 'Tools',
          items: 'spellchecker spellcheckerlanguage | code wordcount',
        },
      },
      statusbar: false,
      content_css: '../../../assets/scss/content-editor-email.css',
      width: '95%',
      paste_as_text: true,
      language:
        this.translate.getCurrentLang() === 'en' ? 'en' : 'th_TH',
      language_url:
        this.translate.getCurrentLang() === 'en'
          ? null
          : 'assets/langs/th_TH.js',
      plugins: [
        'advlist autolink lists link image charmap print preview anchor',
        'searchreplace visualblocks code fullscreen',
        'insertdatetime media table code help wordcount imagetools',
        'textcolor',
      ],
      toolbar:
        'styleselect fontsizeselect | bold italic underline | ',
      style_formats: [
        // Adds a h1 format to style_formats that applies a class of heading
        { title: 'Paragraph', format: 'p' },
        {
          title: 'Heading',
          items: [
            { title: 'Heading 1', block: 'h1', classes: 'heading' },
            { title: 'Heading 2', block: 'h2', classes: 'heading' },
            { title: 'Heading 3', block: 'h3', classes: 'heading' },
            { title: 'Heading 4', block: 'h4', classes: 'heading' },
            { title: 'Heading 5', block: 'h5', classes: 'heading' },
            { title: 'Heading 6', block: 'h6', classes: 'heading' },
          ],
        },
        {
          title: 'Inline',
          items: [
            { title: 'Bold', format: 'bold' },
            { title: 'Italic', format: 'italic' },
            { title: 'Underline', format: 'underline' },
            { title: 'Strikethrough', format: 'strikethrough' },
            { title: 'Superscript', format: 'superscript' },
            { title: 'Subscript', format: 'subscript' },
            { title: 'Code', format: 'code' },
          ],
        },
      ],
      file_picker_types: 'image',
      file_picker_callback: (cb: any) => {
        const input: any = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = (e?: HTMLInputEvent | any) => {
          const file = e.target.files[0];

          const reader = new FileReader();
          reader.onload = (event: any) => {
            const blobInfo = event.target.result;
            cb(blobInfo, { title: file.name });
          };
          reader.readAsDataURL(file);
        };

        input.click();

        interface HTMLInputEvent extends Event {
          target: HTMLInputElement & EventTarget;
        }
      },
      setup: (editor: any) => {
        editor.on('ObjectResized', (e: any) => {
          if (e.target.nodeName === 'IMG') {
            console.log('img', e);
            const selectedImage = editor.selection.getNode();
            editor.dom.setStyle(
              selectedImage,
              'width',
              e.width + 'px',
            );
            editor.dom.setStyle(
              selectedImage,
              'height',
              e.height + 'px',
            );

            selectedImage.removeAttribute('width');
            selectedImage.removeAttribute('height');
          }
        });
      },
      init_instance_callback: (editor: any) => {
        editor.on('ObjectSelected', (e: any) => {
          if (e.target.nodeName === 'IMG') {
            const selectedImage = editor.selection.getNode();
            editor.dom.setStyle(
              selectedImage,
              'width',
              e.target.width + 'px',
            );
            editor.dom.setStyle(
              selectedImage,
              'height',
              e.target.height + 'px',
            );

            selectedImage.removeAttribute('width');
            selectedImage.removeAttribute('height');
          }
        });
        editor.on('keydown', (e: any) => {
          if (e.keyCode === 9) {
            e.preventDefault();
            // @ts-ignore
            tinymce.activeEditor.execCommand(
              'mceInsertContent',
              false,
              '&nbsp;&nbsp;&nbsp;&nbsp;',
            );
          }
        });
        editor.on('input', (e: any) => {
          this.countRemainChar(editor);
        });
        editor.on('keydown', (e: any) => {
          this.countRemainChar(editor);
        });
      },
      automatic_uploads: true,
      fontsize_formats:
        '8pt 10pt 12pt 14pt 16pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt',
      content_style: 'body {font-size: 14pt;}',
      table_toolbar:
        'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow |' +
        ' tableinsertcolbefore tableinsertcolafter tabledeletecol',
      skin: 'lightgray',
    };
    this.configContractTC = {
      height: 200,
      base_url: '/tinymce',
      suffix: '.min',
      icons: 'material', // use icon pack
      skin_url: '/tinymce/skins/lightgray',
      selector: '.editor>textarea',
      branding: false,
      menu: {
        edit: {
          title: 'Edit',
          items:
            'undo redo | cut copy paste | selectall | searchreplace',
        },
      },
      statusbar: false,
      content_css: '../../../assets/scss/content-editor-email.css',
      width: '95%',
      paste_as_text: true,
      language:
        this.translate.getCurrentLang() === 'en' ? 'en' : 'th_TH',
      language_url:
        this.translate.getCurrentLang() === 'en'
          ? null
          : 'assets/langs/th_TH.js',
      plugins: [
        'advlist autolink lists link image charmap print preview anchor',
        'searchreplace visualblocks code fullscreen',
        'insertdatetime media table code help wordcount imagetools',
        'textcolor',
      ],
      toolbar:
        'fontsizeselect | bold italic underline | \
         | removeformat',
      file_picker_types: 'image',
      file_picker_callback: (cb: any) => {
        const input: any = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = (e?: HTMLInputEvent | any) => {
          const file = e.target.files[0];

          const reader = new FileReader();
          reader.onload = (event: any) => {
            const blobInfo = event.target.result;
            cb(blobInfo, { title: file.name });
          };
          reader.readAsDataURL(file);
        };

        input.click();

        interface HTMLInputEvent extends Event {
          target: HTMLInputElement & EventTarget;
        }
      },
      setup: (editor: any) => {
        editor.on('ObjectResized', (e: any) => {
          if (e.target.nodeName === 'IMG') {
            console.log('img', e);
            const selectedImage = editor.selection.getNode();
            editor.dom.setStyle(
              selectedImage,
              'width',
              e.width + 'px',
            );
            editor.dom.setStyle(
              selectedImage,
              'height',
              e.height + 'px',
            );

            selectedImage.removeAttribute('width');
            selectedImage.removeAttribute('height');
          }
        });
      },
      init_instance_callback: (editor: any) => {
        editor.on('ObjectSelected', (e: any) => {
          if (e.target.nodeName === 'IMG') {
            const selectedImage = editor.selection.getNode();
            editor.dom.setStyle(
              selectedImage,
              'width',
              e.target.width + 'px',
            );
            editor.dom.setStyle(
              selectedImage,
              'height',
              e.target.height + 'px',
            );

            selectedImage.removeAttribute('width');
            selectedImage.removeAttribute('height');
          }
        });
        editor.on('keydown', (e: any) => {
          if (e.keyCode === 9) {
            e.preventDefault();
            // @ts-ignore
            tinymce.activeEditor.execCommand(
              'mceInsertContent',
              false,
              '&nbsp;&nbsp;&nbsp;&nbsp;',
            );
          }
        });
        editor.on('input', (e: any) => {
          this.countRemainChar(editor);
        });
        editor.on('keydown', (e: any) => {
          this.countRemainChar(editor);
        });
      },
      automatic_uploads: true,
      fontsize_formats:
        '8pt 10pt 12pt 14pt 16pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt',
      content_style: 'body {font-size: 10pt;}',
      // table_toolbar:
      //   'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow |' +
      //   ' tableinsertcolbefore tableinsertcolafter tabledeletecol',
      skin: 'lightgray',
    };
    this.translate.onLangChange.subscribe({
      next: (event: LangChangeEvent) => {
        const language = event.lang;
        this.config = {
          ...this.config,
          language: language === 'en' ? 'en' : 'th_TH',
          language_url:
            language === 'en' ? null : 'assets/langs/th_TH.js',
        };
        this.configEditorMail = {
          ...this.configEditorMail,
          language: language === 'en' ? 'en' : 'th_TH',
          language_url:
            language === 'en' ? null : 'assets/langs/th_TH.js',
        };
        this.configContractTC = {
          ...this.configContractTC,
          language: language === 'en' ? 'en' : 'th_TH',
          language_url:
            language === 'en' ? null : 'assets/langs/th_TH.js',
        };
        this.isRefreshing = true;
        setTimeout(() => {
          this.isRefreshing = false;
        }, 10);
      },
    });
  }

  onSetContent(event: any) {
    const editor = event.editor;
    this.countRemainChar(editor);
  }

  countRemainChar(editor: any) {
    setTimeout(() => {
      const ZERO_WIDTH_SPACES_REGEX =
        /([\u200B]+|[\u200C]+|[\u200D]+|[\u200E]+|[\u200F]+|[\uFEFF]+)/g;
      const body = editor.contentDocument.body;
      const text = body.innerText.replace(
        ZERO_WIDTH_SPACES_REGEX,
        '',
      );
      if (body.innerText === '\n') {
        this.remain = DEFAULT_REMAIN;
      } else {
        this.remain = DEFAULT_REMAIN - text.length;
      }
      this.charRemainChange.emit(this.remain);
    }, 1);
  }

  editorChange(): void {
    this.store.dispatch(
      new SaveItemMemo(
        this.memoData[this.inputComponent.controlName],
        this.inputComponent.controlName,
      ),
    );
  }

  adjustTableWidthFromPaste(content: string): string {
    const regexp = /<col width="(\d+)/g;
    const array = [...content.matchAll(regexp)];
    const tableWidth = array.reduce((acc, x) => acc + +x[1], 0);
    const tableWidthPercent = (tableWidth * 100) / 655;
    if (tableWidthPercent > 100) {
      const uniqueArray = array.filter(
        (elem, index, self) => index === self.indexOf(elem),
      );
      uniqueArray.forEach((x) => {
        const widthValue = +x[1];
        const newWidthValue = (100 * widthValue) / tableWidthPercent;
        const newWidthStyle = x[0].replace(
          widthValue.toString(),
          ceil(newWidthValue).toString(),
        );
        content = content.replaceAll(x[0], newWidthStyle);
      });
    }
    return content;
  }

  private uploadEditorImage(
    blobInfo: any,
    success: any,
    failure: any,
  ): void {
    const formData = new FormData();
    formData.append('file', blobInfo.blob(), blobInfo.filename());
    this.editorService.uploadEditorImage(formData).subscribe(
      (res: any) => {
        success(res.file);
      },
      (error) => {
        failure('HTTP Error: ' + error.status);
      },
    );
  }
}
